<template>
    <wrapper-features-templates-operations
    :applyErrorState="applyErrorState && !showLoader"
    :customBreadcrumbs="{
        currentArea: params.productName
    }"
    :activeStep="activeStep"
    :feedbackOperationData="headerOperationData"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :hideContentWrapper="isAccountsEmpty"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && !isAccountsEmpty"
    :showFooter="activeStep < 4 && !showLoader && !applyErrorState && !isAccountsEmpty"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('companyPayments.requestTPA.tpaBAI.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, tpaRequest, () => { activeStep = 2 })"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Insert content -->
        <component
        v-if="!applyErrorState && !showLoader && !isAccountsEmpty"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :accountsBalances="getAccountsBalances"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :params="params"
        :mandatoryList="mandatoryList[`step${activeStep}MandatoryList`]"
        :showMandatoryErrors="showMandatoryErrors"
        :responseData="responseData"
        @formDataChange="formDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @addStepValidationError="addStepValidationError"
        @removeStepValidationError="removeStepValidationError"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @showOverlayLoader="showOverlayLoader = true"
        @hideOverlayLoader="showOverlayLoader = false"/>
        <template  #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="!applyErrorState && !showLoader && isAccountsEmpty"
            illustration="emptyState/patrimony"
            :title="t('accountsEmptyStateTitle')"
            :description="t('accountsEmptyStateDescription')"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObject } from '@/assets/scripts/objects'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {	requestAccountsBalances, requestCompanyData, requestLocations, requestAgenciesProvinces, requestBranches, requestEconomicActivities, requestAuthorizedOperations, requestAcceptedCards, clearRequestTPAsBAIFeatureState, validateAmount, tpaRequestSubmit } = mapActions(moduleMapper.COMPANY_PAYMENTS_REQUEST_TPA)
const {	getAccountsBalances, getCompanyData, getAuthorizedOperations, getAcceptedCards } = mapGetters(moduleMapper.COMPANY_PAYMENTS_REQUEST_TPA)
const { getMobileAppState } = mapGetters()

/* props received
@params { Object } - data received from the chosen TPA (TPAsList -> TPA Selected )
*/
const props = defineProps({
    params: Object
})

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)
const isAccountsEmpty = ref(false)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(0)
const stepperParams = ref([
    { description: t('companyPayments.requestTPA.tpaBAI.stepper.data') },
    { description: t('companyPayments.requestTPA.tpaBAI.stepper.informations') },
    { description: t('companyPayments.requestTPA.tpaBAI.stepper.resume') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/companyPayments/requestTPA/BAI/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    originAccount: '',
    currency: '',
    quantity: 1,
    isCalculated: false,
    taxes: '',
    province: '',
    municipality: '',
    requestResponsible: '',
    establishmentName: '',
    establishmentResponsible: '',
    hasTpaInstalled: 2,
    establishmentAddress: '',
    establishmentProvince: '',
    establishmentMunicipality: '',
    establishmentTelephoneNumber: '',
    establishmentMobilePhoneNumber: '',
    activityCodes: '',
    economicActivityOther: '',
    authorizedOperations: [],
    authorizedOperationOther: '',
    cardTypes: [],
    branchProvince: '',
    branchId: '',
    contactPhoneNumber: '',
    notificationEmail: '',
    notes: '',
    canProceed: false
})

const step1MandatoryList = ref([
    'originAccount',
    'quantity'
])

const step2MandatoryList = ref([
    'province',
    'municipality',
    'requestResponsible',
    'establishmentName',
    'establishmentResponsible',
    'hasTpaInstalled',
    'establishmentAddress',
    'establishmentProvince',
    'establishmentMunicipality',
    'establishmentTelephoneNumber',
    'establishmentMobilePhoneNumber',
    'activityCodes',
    'economicActivityOther',
    'authorizedOperations',
    'authorizedOperationOther',
    'cardTypes',
    'branchProvince',
    'branchId',
    'contactPhoneNumber',
    'notificationEmail',
    'canProceed'
])

const mandatoryList = ref({
    step1MandatoryList: step1MandatoryList.value,
    step2MandatoryList: step2MandatoryList.value
})

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

let stepValidationErrors = []

const addStepValidationError = param => {
    if (!stepValidationErrors.find(ele => ele === param)) stepValidationErrors.push(param)
}

const removeStepValidationError = param => {
    if (stepValidationErrors.find(ele => ele === param)) stepValidationErrors.splice(stepValidationErrors.findIndex(ele => ele === param), 1)
}
/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearInputs = () => {
    clearFormErrors.value = true
    stepValidationErrors = []
    clearObject(formData.value)

    formData.value.quantity = 1
    formData.value.isCalculated = false
    formData.value.hasTpaInstalled = 2

    formData.value.authorizedOperations = []
    getAuthorizedOperations.value.forEach(ele => {
        if (ele.default) formData.value.authorizedOperations.push(ele.operationType)
    })

    formData.value.cardTypes = []
    getAcceptedCards.value.forEach(ele => {
        if (ele.default) formData.value.cardTypes.push(ele.parentId)
    })

    /* Reset Origin Account to Default and Reset Number Searched */
    formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
    formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency
}

const validateStep1Inputs = async () => {
    if (!formData.value.isCalculated) {
        errorHandler({
            message: t('companyPayments.requestTPA.tpaBAI.noCalcError'),
            dismissToastBtnText: t('companyPayments.requestTPA.tpaBAI.noCalcButtonLabel')
        })
        return
    }

    if (atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])) {
        showMandatoryErrors.value = true
        return
    }

    try {
        showOverlayLoader.value = true

        await validateAmount({
            accountId: formData.value.originAccount,
            tpaQuantity: formData.value.quantity
        })

        activeStep.value = 2
        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, validateStep1Inputs, () => { showFeedbackToast.value = false })
    }
}

const validateStep2Inputs = () => {
    if (stepValidationErrors.length) return

    const hasEmpty = atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`].filter(ele => ele !== 'economicActivityOther' && ele !== 'authorizedOperationOther'))

    if (hasEmpty ||
        (!formData.value.economicActivityOther.length && formData.value.activityCodes === 'OTHER') ||
        (!formData.value.authorizedOperationOther.length && formData.value.authorizedOperations.includes('OTHER')) ||
        !formData.value.canProceed) {
        showMandatoryErrors.value = true
    } else activeStep.value = 3
}

const tpaRequest = async () => {
    applyErrorState.value = false
	showOverlayLoader.value = true

    const payload = {
        ...formData.value,
        tpaTypeId: props.params.tpaTypeId,
        hasNifValidated: props.params.hasNifValidated,
        accountId: formData.value.originAccount,
        nif: getCompanyData.value.nif,
        companyName: getCompanyData.value.companyName,
        address: getCompanyData.value.socialHeadquartersAddress,
        commercialActivity: getCompanyData.value.commercialActivity,
        accountHolder: getCompanyData.value.accountHolder,
        hasTpaInstalled: formData.value.hasTpaInstalled === 1,
        activityCodes: [formData.value.activityCodes],
        cardTypes: formData.value.cardTypes.reduce((acc, cardParentSelected) => {
            const parent = getAcceptedCards.value.find(obj => obj.parentId === cardParentSelected)
            if (parent) {
                parent.cardTypes.forEach(cardType => {
                    acc.push(cardType.typeCode)
                })
            }
            return acc
        }, [])
    }

	try {
        await tpaRequestSubmit(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, tpaRequest)
	}
}

/* Footer Buttons Data */
const footerButtonsParams = ref([
    {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.goBack'),
        callbackFunction: () => { activeStep.value-- },
        icon: 'arrow-left-full',
        step: 2
    }, {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.goBack'),
        callbackFunction: () => { activeStep.value-- },
        icon: 'arrow-left-full',
        step: 3
    }, {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.forward'),
        callbackFunction: validateStep1Inputs,
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        step: 1
    }, {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.forward'),
        callbackFunction: validateStep2Inputs,
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        step: 2
    }, {
        label: t('companyPayments.requestTPA.tpaBAI.footerButtons.validate'),
        callbackFunction: tpaRequest,
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        step: 3
    }
])

/*************************************************
*                                                *
*                Operation Status                *
*                                                *
*************************************************/
const headerOperationData = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    responseData.value = data

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: data.data.hasReceipt ? {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.tpaRequestProcessView.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.tpaRequestProcessView.operationId } }),
            operationId: data.data.tpaRequestProcessView.operationId,
            shareLinkPayload: 'generateShareLink'
        } : null
    }

    activeStep.value = 4
}

const errorHandler = (data, retryFn, resolveBtnFunction) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, resolveBtnFunction)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                Initial Requets                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
    isAccountsEmpty.value = false
	showLoader.value = true

    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        requestAccountsBalances(),
        requestCompanyData(),
        requestLocations(),
        requestAgenciesProvinces(),
        requestBranches(),
        requestEconomicActivities(),
        requestAuthorizedOperations(),
        requestAcceptedCards()
    ]).then(() => {
        if (getAccountsBalances.value.length > 0) {
            /* Setting Initial Selected Account and Currency */
            formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
            formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency
        } else {
            isAccountsEmpty.value = true
        }

        getAuthorizedOperations.value.forEach(ele => {
            if (ele.default) formData.value.authorizedOperations.push(ele.operationType)
        })

        getAcceptedCards.value.forEach(ele => {
            if (ele.default) formData.value.cardTypes.push(ele.parentId)
        })

        showLoader.value = false
        activeStep.value = 1
    }).catch(error => {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
    })
}

initialRequests()

onBeforeUnmount(() => {
    clearRequestTPAsBAIFeatureState()
})
</script>

<style lang="scss" scoped>
</style>
